import React, { useEffect } from 'react';
import { gql, useQuery, useReactiveVar } from '@apollo/client';
import { Skeleton } from '@chakra-ui/react';

import { logout } from 'sharedHelpers/firebaseAuth';
import {
  maintenanceVar,
  enterMaintenance,
  exitMaintenance,
} from '../graphql/maintenanceVar';
import MaintenancePage from '../../pages/maintenance';

export const APP_STATUS = gql`
  query AppStatus {
    appStatus {
      maintenanceModeEnabled
      title
      message
      retryAfterSeconds
    }
  }
`;

interface AppStatusData {
  appStatus: {
    maintenanceModeEnabled: boolean;
    title: string;
    message: string;
    retryAfterSeconds: number;
  };
}

interface MaintenanceGateProps {
  children: React.ReactNode;
}

function MaintenanceGate({ children }: MaintenanceGateProps): JSX.Element {
  const maintenance = useReactiveVar(maintenanceVar);

  const { data, loading, error, refetch } = useQuery<AppStatusData>(
    APP_STATUS,
    {
      // Only poll while we're down — that's when we're waiting for recovery.
      // In normal operation this runs ONCE on mount
      pollInterval: maintenance ? 60_000 : 0,
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    const status = data?.appStatus;
    if (!status) return;

    if (status.maintenanceModeEnabled) {
      enterMaintenance({
        title: status.title,
        message: status.message,
        retryAfterSeconds: status.retryAfterSeconds,
      });
    } else {
      exitMaintenance();
    }
  }, [data]);

  if (maintenance) {
    return (
      <MaintenancePage
        title={maintenance.title}
        message={maintenance.message}
        retryAfterSeconds={maintenance.retryAfterSeconds}
        onRetry={() => {
          refetch().catch(() => {});
        }}
        onSignOut={logout}
      />
    );
  }

  if (loading && !data && !error) {
    return <Skeleton width="100%" height="100%" />;
  }

  return <>{children}</>;
}

export default MaintenanceGate;
